import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Section, Card, Badge, Button, Input, Label, Alert } from "../design-system";
import { useApi } from "../data/api";
import { useAuth } from "../auth/AuthProvider";
import { useCart } from "../data/cartStore";

function money(v) { return v === null || v === undefined ? "—" : `€ ${Number(v).toFixed(2)}`; }

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function ProductImage({ product, size = 140 }) {
  const src = product.image_url
    ? (product.image_url.startsWith("http") ? product.image_url : `${API_BASE}${product.image_url}`)
    : null;
  if (product.has_image && src) {
    return (
      <div data-testid={`product-image-${product.sku}`}
           style={{ width: "100%", aspectRatio: "4 / 3", borderRadius: 8,
                    background: "#ffffff", border: "1px solid #f1f5f9",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    overflow: "hidden" }}>
        <img src={src} alt={product.name}
             style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto",
                      objectFit: "contain", objectPosition: "center" }}
             onError={(e) => { e.target.style.display = "none"; }} />
      </div>
    );
  }
  return (
    <div data-testid={`product-image-placeholder-${product.sku}`}
         style={{ width: "100%", aspectRatio: "4 / 3", borderRadius: 8, background: "#f3f4f6",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#94a3b8", fontSize: 12, letterSpacing: 0.5 }}>
      NO IMAGE
    </div>
  );
}

// Mar 2026 — Alcune schede prodotto hanno `description` popolato con dati
// numerici/tecnici errati (es. "59" invece di una descrizione commerciale).
// La Customer card mostra description come paragrafo libero: filtrare quei
// valori chiaramente non descrittivi (solo cifre, troppo corti) evita di
// esporre metadata rumorosa al Customer senza modificare il DB.
function isMeaningfulDescription(desc) {
  if (!desc) return false;
  const s = String(desc).trim();
  if (s.length < 4) return false;         // troppo corto per essere descrittivo
  if (/^\d+$/.test(s)) return false;      // solo cifre → non descrizione
  if (/^[\d\s.,-]+$/.test(s)) return false; // solo numeri/punteggiatura
  return true;
}

function TryProductsSection() {
  const { apiGet } = useAuth();
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const add = useCart((s) => s.add);
  const [added, setAdded] = useState(null);
  useEffect(() => {
    apiGet("/api/customer/try-products").then(setItems).catch(() => setItems([]));
  }, [apiGet]);
  if (!items || items.length === 0) return null;
  const VISIBLE = 6;
  const shown = expanded ? items : items.slice(0, VISIBLE);
  const addWithLayer = (p) => {
    const layer = Number(p.cartons_per_layer) || 0;
    const qty = layer >= 1 ? layer : 1;
    add(p, qty);
    setAdded(p.sku);
    setTimeout(() => setAdded(null), 1500);
  };
  return (
    <Section title="Nuovi prodotti da provare"
             subtitle="Scopri alcune referenze che non hai ancora acquistato"
             data-testid="try-products-section">
      <div className="home-grid" data-testid="try-products-grid">
        {shown.map((p) => (
          <Card key={p.id} data-testid={`try-product-${p.sku}`}>
            <Card.Header>
              <ProductImage product={p} size={120} />
            </Card.Header>
            <Card.Body>
              <div className="kicker">{p.sku}</div>
              <h3 style={{ marginTop: ".2rem", fontSize: "1rem" }}>{p.name}</h3>
              <div className="muted" style={{ fontSize: ".78rem", minHeight: "1.3em" }}>
                {p.kg_per_carton ? `${p.kg_per_carton} kg/cart.` : "—"}
                {p.pallet_discount_eligible && <> · <span style={{ color: "#b45309" }}>sconto pedana</span></>}
              </div>
              {p.unit_price != null && (
                <div style={{ fontSize: "1.05rem", fontWeight: 600, marginTop: 4 }}
                     data-testid={`try-price-${p.sku}`}>
                  € {Number(p.unit_price).toFixed(2)}<span style={{ fontSize: ".8rem", opacity: .7 }}> / kg</span>
                </div>
              )}
              <div style={{ display: "flex", gap: ".4rem", marginTop: ".6rem" }}>
                <Link to={`/customer/catalog?sku=${p.sku}`} style={{ flex: 1 }}>
                  <Button variant="outline" size="sm" style={{ width: "100%" }}
                          data-testid={`try-view-${p.sku}`}>Vedi</Button>
                </Link>
                <Button size="sm" data-testid={`try-add-${p.sku}`}
                        onClick={() => addWithLayer(p)}
                        title={p.cartons_per_layer
                                ? `Aggiunge 1 strato = ${p.cartons_per_layer} cartoni`
                                : "Aggiunge 1 cartone"}>
                  {added === p.sku
                    ? `✓ +${p.cartons_per_layer || 1}`
                    : (p.cartons_per_layer ? `+ Strato (${p.cartons_per_layer})` : "+ Aggiungi")}
                </Button>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
      {items.length > VISIBLE && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: ".8rem" }}>
          <Button variant="ghost" size="sm" data-testid="try-see-more"
                  onClick={() => setExpanded(!expanded)}>
            {expanded ? "Mostra meno" : `Vedi altri prodotti (${items.length - VISIBLE})`}
          </Button>
        </div>
      )}
    </Section>
  );
}

export default function CatalogPage() {
  const api = useApi();
  const { apiGet } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [q, setQ] = useState("");
  const [activeQuery, setActiveQuery] = useState(""); // query effettivamente applicata sul backend
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const add = useCart((s) => s.add);
  const setQty = useCart((s) => s.setQty);
  const removeLine = useCart((s) => s.remove);
  const cartLines = useCart((s) => s.lines);
  // Feb 2026 UX — contatore live "Vai al carrello (N)": somma dei colli in carrello,
  // NON il numero di SKU. Aggiornato istantaneamente ad ogni add/qty change/remove.
  const totalCartons = useMemo(
    () => cartLines.reduce((s, l) => s + (Number(l.qty_cartons) || 0), 0),
    [cartLines],
  );
  // Mappa product_id → qty per l'hint "Nel carrello: N colli" sulla card.
  const cartQtyByProduct = useMemo(() => {
    const m = new Map();
    for (const l of cartLines) m.set(l.product_id, Number(l.qty_cartons) || 0);
    return m;
  }, [cartLines]);
  const [added, setAdded] = useState(null);

  // Feb 2026 — categorie: usa esclusivamente le categorie reali già
  // associate ai prodotti. NON creare categorie hardcoded.
  const usedCategoryIds = useMemo(() => {
    const s = new Set();
    for (const p of products) if (p.category_id) s.add(p.category_id);
    return s;
  }, [products]);
  const activeCategories = useMemo(
    () => (categories || []).filter(c => usedCategoryIds.has(c.id))
                            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)
                                          || a.name.localeCompare(b.name, "it")),
    [categories, usedCategoryIds],
  );

  // Feb 2026 — mostra SOLO prodotti realmente acquistabili: la ricerca è
  // globale su tutto l'ACTIVE catalog (backend), ma un prodotto senza prezzo
  // risolvibile per il cliente NON deve comparire. Il backend restituisce
  // già `unit_price=null` in quel caso; filtriamo qui per rispettare la
  // regola "SOLO se il sistema è in grado di determinare il prezzo".
  const purchasableProducts = useMemo(
    () => products.filter(p => p.unit_price != null && p.unit_price !== ""),
    [products],
  );

  // Filtro client-side per categoria (opera su ciò che ha già superato il
  // filtro pricing). Search + categoria lavorano insieme.
  const filteredProducts = useMemo(() => {
    if (!selectedCategoryId) return purchasableProducts;
    return purchasableProducts.filter(p => p.category_id === selectedCategoryId);
  }, [purchasableProducts, selectedCategoryId]);

  const load = async (query = "") => {
    setLoading(true); setErr(null);
    try {
      const res = await api.catalog(query);
      setProducts(res);
      setActiveQuery(query || "");
    }
    catch (e) { setErr(e?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(""); }, []);
  useEffect(() => {
    apiGet("/api/categories").then(setCategories).catch(() => setCategories([]));
  }, [apiGet]);

  // Feb 2026 — se il cliente svuota il campo ricerca, torna automaticamente
  // alla vista catalogo standard (comportamento chiesto esplicitamente).
  useEffect(() => {
    if (q === "" && activeQuery !== "") {
      load("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const isGlobalSearch = activeQuery.trim().length > 0;

  const onAdd = (p) => {
    // Spec §3: quantità standard = 1 strato completo se disponibile.
    // Fallback: 1 cartone quando cartons_per_layer manca (non inventare valori).
    // Il bottone NON è disabilitato: click ripetuti accumulano strati (Feb 2026).
    const layer = Number(p.cartons_per_layer) || 0;
    const qty = layer >= 1 ? layer : 1;
    add(p, qty);
    setAdded(p.sku);
    setTimeout(() => setAdded(null), 1500);
  };

  return (
    <>
      {/* Sticky bar: search + Vai al carrello con contatore live + chip categorie.
          Un'unica area sticky compatta per evitare doppie sticky sovrapposte. */}
      <div data-testid="catalog-sticky-bar"
           style={{ position: "sticky", top: 0, zIndex: 20,
                    background: "#ffffff",
                    padding: "0.6rem 0", marginBottom: "0.75rem",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", gap: ".5rem", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 260px", display: "flex", gap: ".5rem" }}>
            <Input data-testid="catalog-search" placeholder="Cerca SKU o nome…"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") load(q); }}
                   style={{ flex: 1 }} />
            <Button variant="outline" onClick={() => load(q)} data-testid="catalog-search-btn">Cerca</Button>
          </div>
          <Link to="/customer/cart" data-testid="go-cart-link">
            <Button variant="primary" data-testid="go-cart"
                    title={`${totalCartons} cartoni totali nel carrello`}>
              Vai al carrello (<span data-testid="go-cart-count">{totalCartons}</span>)
            </Button>
          </Link>
        </div>
        {isGlobalSearch && (
          <div data-testid="catalog-global-search-hint"
               style={{ fontSize: ".78rem", color: "#0F4C81",
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "2px 4px" }}>
            <span aria-hidden="true">🔎</span>
            Ricerca su tutto il catalogo attivo per <strong>"{activeQuery}"</strong>
            {filteredProducts.length > 0 && (
              <span className="muted">· {filteredProducts.length} prodotti trovati</span>
            )}
          </div>
        )}
        {activeCategories.length > 0 && (
          <div data-testid="catalog-categories-bar"
               style={{ display: "flex", gap: 6, alignItems: "center",
                        overflowX: "auto", paddingBottom: 2,
                        WebkitOverflowScrolling: "touch" }}>
            <Button size="sm"
                    variant={selectedCategoryId === "" ? "primary" : "outline"}
                    data-testid="catalog-cat-all"
                    onClick={() => setSelectedCategoryId("")}>
              Tutti
            </Button>
            {activeCategories.map(c => (
              <Button key={c.id} size="sm"
                      variant={selectedCategoryId === c.id ? "primary" : "outline"}
                      data-testid={`catalog-cat-${c.code || c.id}`}
                      onClick={() => setSelectedCategoryId(c.id)}
                      style={{ flexShrink: 0 }}>
                {c.name}
              </Button>
            ))}
          </div>
        )}
      </div>

      <Section title="Catalogo" subtitle="Prodotti attivi con prezzo al tuo listino">
        {err && <Alert tone="danger">{err}</Alert>}
        {loading && <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>}
        {!loading && filteredProducts.length === 0 && (
          <Card data-testid="catalog-empty"><Card.Body className="muted">
            Nessun prodotto per questi filtri.
            {selectedCategoryId && (
              <> <a href="#" onClick={(e) => { e.preventDefault(); setSelectedCategoryId(""); }}
                    data-testid="catalog-cat-clear" style={{ color: "#0F4C81" }}>Torna a Tutti</a>.</>
            )}
          </Card.Body></Card>
        )}
      </Section>

      <div className="home-grid" data-testid="catalog-grid">
        {filteredProducts.map((p) => {
          const inCart = cartQtyByProduct.get(p.id) || 0;
          return (
          <Card key={p.id} data-testid={`product-${p.sku}`}>
            <Card.Header>
              <ProductImage product={p} size={120} />
              <div style={{ display: "flex", justifyContent: "space-between", gap: ".5rem",
                            alignItems: "center", marginTop: ".5rem" }}>
                <div>
                  <div className="kicker">{p.sku}</div>
                  <h3 style={{ marginTop: ".2rem" }}>{p.name}</h3>
                </div>
                <div style={{ display: "flex", gap: ".3rem", flexWrap: "wrap",
                              justifyContent: "flex-end" }}>
                  {p.in_purchase_history === true && (
                    <Badge tone="success"
                           data-testid={`badge-purchased-${p.sku}`}>Già acquistato</Badge>
                  )}
                  {p.in_purchase_history === false && (
                    <Badge tone="info"
                           data-testid={`badge-not-purchased-${p.sku}`}>Mai acquistato</Badge>
                  )}
                  {p.pfa_eligible && <Badge tone="success">PFA</Badge>}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {isMeaningfulDescription(p.description) && (
                <p className="muted" style={{ fontSize: ".9rem", minHeight: "2.5rem" }}
                   data-testid={`product-desc-${p.sku}`}>{p.description}</p>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: ".5rem" }}>
                <div>
                  <div style={{ fontSize: "1.5rem", fontFamily: "var(--ds-font-display)", fontWeight: 600 }}
                       data-testid={`price-per-kg-${p.sku}`}>
                    {money(p.unit_price)}<span style={{ fontSize: "1rem", opacity: .7 }}> / kg</span>
                  </div>
                  <div className="muted" style={{ fontSize: ".8rem" }}>
                    Cartone: {p.kg_per_carton ?? "—"} kg
                    {p.unit_price && p.kg_per_carton
                      ? <> · ≈ {money(Number(p.unit_price) * Number(p.kg_per_carton))} / cartone</>
                      : null}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {inCart > 0 ? (
                    <>
                      <Button size="sm" variant="outline"
                              data-testid={`dec-${p.sku}`}
                              title="Diminuisci"
                              onClick={() => {
                                const step = Number(p.cartons_per_layer) || 1;
                                const next = inCart - step;
                                if (next <= 0) removeLine(p.id);
                                else setQty(p.id, next);
                              }}>−</Button>
                      <span data-testid={`qty-${p.sku}`}
                            style={{ minWidth: 46, textAlign: "center", fontWeight: 700 }}>
                        {inCart}
                      </span>
                      <Button size="sm" data-testid={`add-${p.sku}`}
                              title={p.cartons_per_layer
                                ? `+${p.cartons_per_layer} cart.`
                                : "+1 cart."}
                              onClick={() => onAdd(p)}>+</Button>
                      <Button size="sm" variant="ghost"
                              data-testid={`remove-${p.sku}`}
                              title="Rimuovi dal carrello"
                              onClick={() => removeLine(p.id)}
                              style={{ color: "#b91c1c" }}>Rimuovi</Button>
                    </>
                  ) : (
                    <Button data-testid={`add-${p.sku}`} onClick={() => onAdd(p)}
                            title={p.cartons_per_layer
                                    ? `Aggiunge 1 strato = ${p.cartons_per_layer} cartoni (click multipli accumulano)`
                                    : "Aggiunge 1 cartone (click multipli accumulano)"}>
                      {added === p.sku
                        ? `✓ +${p.cartons_per_layer || 1} cart.`
                        : (p.cartons_per_layer
                           ? `Aggiungi strato (${p.cartons_per_layer})`
                           : "Aggiungi")}
                    </Button>
                  )}
                </div>
              </div>
              {/* Feb 2026 — hint contatore per-prodotto: mostra il totale
                  accumulato in carrello dopo click ripetuti, così l'utente
                  sa che i click stanno effettivamente sommando. */}
              {inCart > 0 && (
                <div data-testid={`in-cart-hint-${p.sku}`}
                     style={{ marginTop: 6, fontSize: ".78rem",
                              color: "#0F4C81", fontWeight: 500 }}>
                  Nel carrello: {inCart} colli
                </div>
              )}
              <div style={{ marginTop: ".75rem", display: "flex", gap: ".4rem", flexWrap: "wrap" }}>
                <Badge>Cartoni/strato: {p.cartons_per_layer ?? "—"}</Badge>
                <Badge>Strati pallet: {p.layers_per_std_pallet ?? "—"}</Badge>
                {p.pallet_discount_eligible && <Badge tone="warning">Sconto pallet monoreferenza</Badge>}
              </div>
              {p.cartons_per_std_pallet && p.cartons_per_std_pallet > 0 && (
                <div style={{ marginTop: ".75rem", padding: ".6rem",
                              border: "1px solid #d1d5db", borderRadius: 8,
                              background: p.pallet_discount_eligible ? "#f0fdf4" : "#fafafa" }}
                     data-testid={`pallet-cta-${p.sku}`}>
                  <div className="muted" style={{ fontSize: ".8rem", marginBottom: 6 }}>
                    Pedana intera: <strong>{p.cartons_per_std_pallet} cart.</strong>
                    {p.kg_per_carton
                      ? <> · {(Number(p.cartons_per_std_pallet) * Number(p.kg_per_carton)).toFixed(1)} kg</>
                      : null}
                  </div>
                  {/* Feb 2026 — CTA pedana in variante accent per essere più
                      evidente rispetto ad "Aggiungi strato" (primary). Il bottone
                      NON si disabilita: click multipli accumulano pedane. */}
                  <Button size="md" variant="accent"
                          data-testid={`add-pallet-${p.sku}`}
                          onClick={() => { add(p, Number(p.cartons_per_std_pallet)); setAdded(p.sku);
                                            setTimeout(() => setAdded(null), 1500); }}
                          title={`Aggiunge ${p.cartons_per_std_pallet} cartoni (1 pedana intera). Click multipli accumulano più pedane.`}>
                    Ordina una pedana ({p.cartons_per_std_pallet} colli)
                  </Button>
                  {p.pallet_discount_eligible && (
                    <div data-testid={`pallet-discount-hint-${p.sku}`}
                         style={{ marginTop: 6, fontSize: ".78rem",
                                  color: "#0F7B4A", fontWeight: 500 }}>
                      ✓ Pedana completa · risparmio pallet monoreferenza
                      <div className="muted" style={{ fontWeight: 400, fontSize: ".72rem", marginTop: 2 }}>
                        Lo sconto reale è calcolato dal listino nel carrello.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        );})}
      </div>
    </>
  );
}